import {Component, OnDestroy, OnInit} from '@angular/core';
import {PhoneNumberFormat, PhoneNumberUtil} from 'google-libphonenumber';
import {CountryISO, TextLabels} from 'ngx-material-intl-tel-input';
import {LanguageType, RoleType, UserMutationInput} from '../../../shared/graphql/globalTypes';
import gql from 'graphql-tag';
import {Apollo} from 'apollo-angular';
import {
  GetUserEdit,
  GetUserEdit_me,
  GetUserEdit_user,
  GetUserEditVariables,
} from './__generated__/GetUserEdit';
import {ActivatedRoute, Router} from '@angular/router';
import {EMPTY, Subject, Subscription} from 'rxjs';
import {FileInput} from '../create-user/create-user.component';
import {TypedFormGroup, typedFormGroup} from 'ngx-forms-typed';
import {UntypedFormControl, Validators} from '@angular/forms';
import {catchError, exhaustMap, map, take} from 'rxjs/operators';
import {UpdateUser, UpdateUserVariables} from './__generated__/UpdateUser';
import {ToastrService} from 'ngx-toastr';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ImageCropComponent} from '../../../shared/modals/image-crop/image-crop.component';
import {notNull} from '../../../shared/utils/utils';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {userListFrag} from '../users.component';
import {ErrorService} from '../../../shared/services/error.service';
import {LocalizationService} from '../../../shared/services/localization.service';
import {
  labelFrag,
  MultiSelectType,
} from '../../../shared/components/multi-select/multi-select.component';
import {MultiSelectComponentLabelFragment} from '../../../shared/components/multi-select/__generated__/MultiSelectComponentLabelFragment';

type UpdateUserData = Required<
  Omit<UserMutationInput, 'clientMutationId' | 'id' | 'photoBase64' | 'photoFilename' | 'labels'>
> & {labels: MultiSelectComponentLabelFragment[]};

interface FormData {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  form: any;
  photo: FileInput[];
}

export const userEditFrag = gql`
  fragment UserEditFragment on UserType {
    id
    firstName
    lastName
    email
    function
    language
    phone
    photo
    role
    labels {
      edges {
        node {
          ...MultiSelectComponentLabelFragment
        }
      }
    }
  }
  ${labelFrag}
`;

const EditUserPostQuery = gql`
  mutation UpdateUser($input: UserMutationInput!) {
    updateOrCreateUser(input: $input) {
      user {
        ...UserEditFragment
        ...UserListFragment
      }
      errors {
        field
        messages
      }
    }
  }
  ${userEditFrag}
  ${userListFrag}
`;

const userEditGetQuery = gql`
  query GetUserEdit($id: ID!) {
    user(id: $id) {
      ...UserEditFragment
    }
    me {
      id
      firstName
      lastName
      role
      tenant {
        id
      }
    }
    labels {
      edges {
        node {
          ...MultiSelectComponentLabelFragment
        }
      }
    }
  }
  ${userEditFrag}
  ${labelFrag}
`;

const phoneTextLabels: TextLabels = {
  mainLabel: '',
  codePlaceholder: 'Code',
  searchPlaceholderLabel: 'Search',
  noEntriesFoundLabel: 'No countries found',
  nationalNumberLabel: '',
  hintLabel: '',
  invalidNumberError: 'Number is not valid',
  requiredError: 'This field is required',
};

@Component({
  selector: 'app-profile-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss'],
  standalone: false,
})
export class UserEditComponent implements OnInit, OnDestroy {
  admin = false;
  superAdmin = false;
  CountryISO = CountryISO;
  phoneTextLabels = phoneTextLabels;
  private readonly phoneNumberUtil = PhoneNumberUtil.getInstance();
  initialLanguage: LanguageType | null = null;
  language: LanguageType | null = null;
  LanguageType = LanguageType;
  user: GetUserEdit_user | null = null;
  currentUser: GetUserEdit_me | null = null;
  userIsCurrentUser = false;
  userIsAdmin = false;
  formData$ = new Subject<FormData>();
  role: RoleType | null = null;
  files: FileInput[] = [];
  photoDeleted = false;
  bsModalRef: BsModalRef | null = null;
  base64 = '';
  labels: MultiSelectComponentLabelFragment[] = [];
  RoleType = RoleType;
  MultiSelectType = MultiSelectType;
  private subscriptions: Subscription[] = [];

  form = typedFormGroup({
    firstName: new UntypedFormControl('', Validators.required),
    lastName: new UntypedFormControl('', Validators.required),
    function: new UntypedFormControl(''),
    email: new UntypedFormControl('', [Validators.email, Validators.required]),
    phone: new UntypedFormControl(null),
    role: new UntypedFormControl(RoleType.USER, Validators.required),
    language: new UntypedFormControl(LanguageType.ENGLISH, Validators.required),
    labels: new UntypedFormControl([]),
  }) as TypedFormGroup<UpdateUserData>;

  constructor(
    private apollo: Apollo,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private formHelperService: FormHelperService,
    private errService: ErrorService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.form.controls.phone.valueChanges.pipe(take(1)).subscribe(() => {
        this.form.controls.phone.markAsPristine();
        this.form.controls.phone.markAsUntouched();
      }),
      this.apollo
        .query<GetUserEdit, GetUserEditVariables>({
          query: userEditGetQuery,
          variables: {
            id: this.route.snapshot.params.id,
          },
        })
        .pipe(
          take(1),
          catchError(error => {
            this.errService.handleDefault(error);
            return EMPTY;
          }),
        )
        .subscribe(data => {
          this.user = data.data?.user ?? null;
          this.currentUser = data.data?.me ?? null;
          if (this.currentUser === null) {
            return;
          }
          if (
            this.currentUser.role === RoleType.USER &&
            (!this.user || this.user.id !== this.currentUser.id)
          ) {
            this.toastr.error($localize`Unauthorized`);
            this.router.navigate(['users']);
          }
          if (this.user !== null) {
            this.setFormControls();
            this.language = this.user.language;

            if (this.initialLanguage === null) {
              this.initialLanguage = this.user.language;
            }
            this.role = this.user.role;

            if (this.user.id === this.currentUser.id) {
              this.userIsCurrentUser = true;
            }
            if (
              this.currentUser.role === RoleType.ADMIN ||
              this.currentUser.role === RoleType.SUPERADMIN
            ) {
              this.userIsAdmin = true;
            }
            if (this.user.photo === null) {
              this.photoDeleted = true;
            }
            if (this.user.role === RoleType.ADMIN) {
              this.admin = true;
            }
            if (this.user.role === RoleType.SUPERADMIN) {
              this.superAdmin = true;
            }
            this.labels = (data.data?.labels.edges ?? []).map(edge => edge.node);
          }
        }),
      this.formData$
        .pipe(
          map((data: FormData) => {
            return {
              id: this.user?.id,
              email: data.form.email,
              firstName: data.form.firstName,
              function: data.form.function,
              language: this.language,
              lastName: data.form.lastName,
              phone: this.getPhoneNumber(data.form.phone),
              role: this.role,
              photoFilename: this.files[0]?.filename,
              photoBase64: this.files[0]?.base64,
              labels: data.form.labels.map((label: MultiSelectComponentLabelFragment) => label.id),
            } as Required<UserMutationInput>;
          }),
          exhaustMap(data => {
            return this.apollo
              .mutate<UpdateUser, UpdateUserVariables>({
                mutation: EditUserPostQuery,
                variables: {
                  input: data,
                },
              })
              .pipe(
                catchError(error => {
                  this.errService.handleDefault(error);
                  return EMPTY;
                }),
              );
          }),
        )
        .subscribe(data => {
          if (notNull(data.data).updateOrCreateUser.errors.length > 0) {
            this.formHelperService.handleServerErrors(
              this.form,
              notNull(data.data).updateOrCreateUser.errors,
            );
            this.toastr.error($localize`Something went wrong`);
          } else {
            this.toastr.success($localize`User edited`);
            if (this.userIsCurrentUser && this.initialLanguage !== this.language) {
              if (this.language) {
                // Url block has trailing slash, redirects the user to the users page in the correct language.
                window.location.href = `/${
                  LocalizationService.getLocaleDetails(this.language).urlBlock
                }users`;
              }
            } else {
              this.router.navigate(['users']);
            }
          }
        }),
    );

    if (this.user !== null) {
      this.language = this.user.language;
    }
  }

  openModal(): void {
    this.bsModalRef = this.modalService.show(ImageCropComponent);
    this.bsModalRef.content.base64 = this.base64;
    this.bsModalRef.content.callback = (base64: string) => {
      if (base64 !== '') {
        this.files[0].base64 = base64;
      } else {
        this.photoDeleted = true;
        this.files = [{base64: null, filename: null}];
      }
    };
  }

  onSubmit(): void {
    if (!this.superAdmin) {
      this.role = this.admin ? RoleType.ADMIN : RoleType.USER;
    } else {
      this.role = RoleType.SUPERADMIN;
    }

    if (this.role !== null && this.user !== null) {
      this.formData$.next({
        form: this.form.value,
        photo: this.files,
      });
      return;
    }
  }

  setFormControls(): void {
    if (this.user !== null) {
      this.form.controls.firstName.setValue(this.user.firstName);
      this.form.controls.lastName.setValue(this.user.lastName);
      this.form.controls.function.setValue(this.user.function);
      this.form.controls.phone.setValue(this.getPhoneInputValue(this.user.phone));
      this.form.controls.email.setValue(this.user.email);
      this.form.controls.role.setValue(this.user.role);
      this.form.controls.language.setValue(this.user.language);
      this.form.controls.labels.setValue(this.user.labels.edges.map(edge => edge.node));
    }
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  imageUpload(event: any): void {
    this.photoDeleted = false;
    const files = event.target.files;
    this.files = [];
    for (let i = 0; i !== files.length; i++) {
      const file = files[i];
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        () => {
          this.files.push({
            base64: reader.result as string,
            filename: file.name,
          });
          this.base64 = reader.result as string;
          event.target.value = '';
          this.openModal();
        },
        false,
      );
      reader.readAsDataURL(files[i]);
    }
  }

  toggleAdmin(): void {
    this.admin = !this.admin;
  }

  removePhoto(): void {
    if (confirm($localize`Are you sure you want to delete the photo?`)) {
      this.photoDeleted = true;
      this.files = [{base64: null, filename: null}];
    }
  }

  navigateBack(): void {
    this.router.navigate(['users']);
  }

  private getPhoneNumber(phone: unknown): string {
    if (typeof phone !== 'string' || phone.trim() === '') {
      return '';
    }

    return phone;
  }

  private getPhoneInputValue(phone: string | null): string {
    if (!phone) {
      return '';
    }

    try {
      const parsedPhone = this.phoneNumberUtil.parse(phone, CountryISO.Netherlands.toUpperCase());
      return this.phoneNumberUtil.format(parsedPhone, PhoneNumberFormat.INTERNATIONAL);
    } catch {
      return phone;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
